import { Stack, Text, Card, Group, Loader, Alert, Button, Box } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { IconFileSpreadsheet, IconUpload, IconX, IconCheck, IconDownload } from '@tabler/icons-react';
import { shapeKorttitoimitus, type ShapeResult } from '../api/korttitoimitus';
import { PageHeader } from '../components/PageHeader';
import { ErrorState } from '../components/ErrorState';
import { t } from '../lib/strings';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function KorttitoimitusPage() {
  const [result, setResult] = useState<ShapeResult | null>(null);
  const mutation = useMutation({
    mutationFn: shapeKorttitoimitus,
    onSuccess: (r) => { setResult(r); downloadBlob(r.filename, r.blob); },
  });

  function onDrop(files: File[]): void {
    const file = files[0];
    if (!file) return;
    setResult(null);
    mutation.mutate(file);
  }

  const husTotal = result ? result.stats.perTab.reduce((s, x) => s + x.husRemoved, 0) : 0;

  return (
    <Stack gap="md">
      <PageHeader title={t.korttitoimitus.title} subtitle={t.korttitoimitus.subtitle} />

      <Card withBorder>
        <Text size="sm" c="dimmed">{t.korttitoimitus.help}</Text>
      </Card>

      <Dropzone
        onDrop={onDrop}
        onReject={() => notifications.show({ color: 'red', message: t.errors.fileRejected })}
        accept={{ [XLSX_MIME]: ['.xlsx'] }}
        maxFiles={1}
        maxSize={50 * 1024 * 1024}
        loading={mutation.isPending}
      >
        <Group justify="center" gap="md" mih={120} style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept><IconUpload size={32} /></Dropzone.Accept>
          <Dropzone.Reject><IconX size={32} /></Dropzone.Reject>
          <Dropzone.Idle><IconFileSpreadsheet size={32} /></Dropzone.Idle>
          <Stack gap={0} align="center">
            <Text size="lg">{t.korttitoimitus.drop}</Text>
            <Text size="sm" c="dimmed">{t.korttitoimitus.hint}</Text>
          </Stack>
        </Group>
      </Dropzone>

      {mutation.isPending && (
        <Card withBorder bg="blue.0">
          <Group gap="sm"><Loader size="sm" /><Text fw={600} c="blue.9">{t.korttitoimitus.processing}</Text></Group>
        </Card>
      )}

      {mutation.isError && <ErrorState error={mutation.error} />}

      {result && (
        <Alert icon={<IconCheck size={20} />} color="green" variant="light" title={t.korttitoimitus.doneTitle}>
          <Stack gap="sm">
            <Group gap="xl">
              <Stat label={t.korttitoimitus.outputRows} value={result.stats.outputRows} />
              <Stat label={t.korttitoimitus.removedHus} value={husTotal} />
              <Stat label={t.korttitoimitus.skippedPlus} value={result.stats.plusSkipped} />
            </Group>
            <Text size="xs" c="dimmed">
              {t.korttitoimitus.perTab}: {result.stats.perTab.map((x) => `${x.name} → ${x.kept}/${x.read}`).join(' · ')}
            </Text>
            <Group>
              <Button
                leftSection={<IconDownload size={16} />}
                variant="light"
                onClick={() => downloadBlob(result.filename, result.blob)}
              >
                {t.korttitoimitus.download}
              </Button>
              <Text size="sm" c="dimmed">{result.filename}</Text>
            </Group>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Box>
      <Text fz={22} fw={700}>{value}</Text>
      <Text size="xs" c="dimmed">{label}</Text>
    </Box>
  );
}
